import React, { useEffect, useMemo, useState } from "react";

const inferType = (fileName = "", mimeType = "") => {
  const lowerName = String(fileName || "").toLowerCase();
  const lowerMime = String(mimeType || "").toLowerCase();
  if (lowerMime.startsWith("image/") || lowerName.match(/\.(png|jpg|jpeg|gif|webp)$/)) return "image";
  if (lowerMime.includes("pdf") || lowerName.endsWith(".pdf")) return "pdf";
  if (lowerMime.startsWith("video/") || lowerName.match(/\.(mp4|mov|mkv|webm)$/)) return "video";
  if (lowerMime.startsWith("audio/") || lowerName.match(/\.(mp3|wav|ogg|m4a|aac)$/)) return "audio";
  return "file";
};

const base64ToBlobUrl = (base64, mimeType) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: mimeType || "application/octet-stream" });
  return URL.createObjectURL(blob);
};

export default function QuickLookModal({ open, attachment, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");

  const type = useMemo(
    () => inferType(attachment?.fileName || attachment?.name, attachment?.mimeType || attachment?.mime_type),
    [attachment?.fileName, attachment?.mimeType, attachment?.mime_type, attachment?.name]
  );

  useEffect(() => {
    if (!open) return;
    const base64 = attachment?.contentBase64 || attachment?.content_base64 || "";
    const url = attachment?.url || "";
    const mime = attachment?.mimeType || attachment?.mime_type || "application/octet-stream";
    if (base64) {
      const u = base64ToBlobUrl(base64, mime);
      setBlobUrl(u);
      return () => {
        try {
          URL.revokeObjectURL(u);
        } catch (_e) {}
      };
    }
    setBlobUrl(url);
    return undefined;
  }, [open, attachment]);

  if (!open) return null;

  const title = attachment?.fileName || attachment?.name || "Attachment";
  const url = blobUrl || attachment?.url || "";

  return (
    <div className="fixed inset-0 z-[10000] bg-black/55 backdrop-blur-[2px] flex items-end md:items-center justify-center p-3">
      <button className="absolute inset-0" aria-label="Close preview" onClick={onClose} />
      <div className="relative w-[min(920px,96vw)] h-[min(86vh,760px)] bg-[#0b1220] text-white rounded-[28px] border border-white/10 shadow-[0_26px_90px_rgba(0,0,0,0.55)] overflow-hidden">
        <div className="h-12 px-4 flex items-center gap-2 border-b border-white/10">
          <div className="font-semibold text-sm truncate">{title}</div>
          <div className="ml-auto flex items-center gap-2">
            {url ? (
              <a
                href={url}
                download={title}
                className="text-xs px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/10"
              >
                Download
              </a>
            ) : null}
            <button className="text-lg leading-none w-8 h-8 rounded-full hover:bg-white/10" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className="h-[calc(100%-48px)] bg-black flex items-center justify-center">
          {!url ? (
            <div className="text-white/70 text-sm p-6 text-center">
              No preview available for this attachment yet.
            </div>
          ) : type === "image" ? (
            <img src={url} alt={title} className="max-h-full max-w-full object-contain" />
          ) : type === "pdf" ? (
            <iframe title={title} src={url} className="w-full h-full bg-white" />
          ) : type === "video" ? (
            <video src={url} controls className="max-h-full max-w-full" />
          ) : type === "audio" ? (
            <div className="w-full max-w-[520px] p-6">
              <audio src={url} controls className="w-full" />
            </div>
          ) : (
            <div className="text-white/70 text-sm p-6 text-center">
              Preview not supported. Use download.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

